import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2 } from "lucide-react";
import { publicSignInfo, publicSign, publicSignReject } from "../api/accounting";
import { MyBillSignHeader } from "../features/acct/MyBillSignHeader";
import { MyBillSignAmount } from "../features/acct/MyBillSignAmount";
import { MyBillSignDetails } from "../features/acct/MyBillSignDetails";
import { MyBillSignInfo } from "../features/acct/MyBillSignInfo";
import { MyBillSignTerms } from "../features/acct/MyBillSignTerms";
import { MyBillSignActions } from "../features/acct/MyBillSignActions";
import { PublicBillNameInput } from "../features/acct/PublicBillNameInput";
import { SignatureCanvas } from "../features/pay/SignatureCanvas";

// صفحة توقيع عامة — نفس تصميم صفحة الموظف الداخلي بالضبط، بدون ازدواجية.
export default function PublicBillSignPage() {
  const params = useParams();
  const key = params.slug ? "slug" : "token";
  const value = params.slug || params.token;
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [name, setName] = useState("");
  const [sig, setSig] = useState(null);
  const [terms, setTerms] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    document.documentElement.classList.remove("dark");
    publicSignInfo({ [key]: value })
      .then((d) => { setData(d); setName(d.bill.receiver_name || ""); })
      .catch((e) => setError(e.response?.data?.detail || "invalid_or_expired_token"));
  }, [key, value]);

  const confirm = async () => {
    if (!name.trim()) { toast.error(t("acct.bills.sign.needName")); return; }
    if (!sig) { toast.error(t("acct.bills.sign.needSig")); return; }
    if (!terms) { toast.error(t("acct.bills.mysign.needTerms")); return; }
    setBusy(true);
    try { await publicSign({ [key]: value, signature_dataurl: sig, receiver_name: name.trim() });
      toast.success(t("acct.bills.mysign.confirmed")); setDone(true);
    } catch (e) { toast.error(e.response?.data?.detail || "error"); } finally { setBusy(false); }
  };
  const reject = async () => {
    const reason = window.prompt(t("acct.bills.mysign.rejectAsk"), "");
    if (!reason || !reason.trim()) return;
    setBusy(true);
    try { await publicSignReject({ [key]: value, reason: reason.trim() });
      toast(t("acct.bills.mysign.rejected")); setDone(true);
    } catch (e) { toast.error(e.response?.data?.detail || "error"); } finally { setBusy(false); }
  };

  if (error) return (
    <div className="mx-auto mt-16 max-w-md rounded-3xl border-2 border-rose-400 bg-white p-6 text-center shadow-lg" data-testid="public-sign-error">
      <AlertTriangle className="mx-auto mb-3 h-10 w-10 text-rose-500" />
      <p className="text-lg font-black text-rose-600">{t("acct.bills.share.expired")}</p>
    </div>
  );
  if (!data) return <div className="grid min-h-screen place-content-center text-sm">…</div>;
  if (done) return (
    <div className="mx-auto mt-16 max-w-md rounded-3xl border-2 border-emerald-500 bg-white p-6 text-center shadow-lg" data-testid="public-sign-done">
      <CheckCircle2 className="mx-auto mb-3 h-12 w-12 text-emerald-600" />
      <p className="text-lg font-black text-emerald-700">{t("acct.bills.sign.done")}</p>
    </div>
  );

  const b = data.bill;
  return (
    <div className="min-h-screen bg-slate-50 pb-28" data-testid="public-sign-page">
      <MyBillSignHeader bill={b} showBack={false} />
      <div className="mx-auto max-w-2xl space-y-4 p-4">
        <MyBillSignAmount bill={b} company={data.company} cashbox={data.cashbox} branch={data.branch} />
        <MyBillSignDetails bill={b} cashbox={data.cashbox} branch={data.branch} />
        <MyBillSignInfo bill={b} cashbox={data.cashbox} sentBy={data.sent_by} sentAt={data.sent_at} />
        <PublicBillNameInput value={name} setValue={setName} locked={Boolean(b.receiver_name)} />
        <div className="rounded-2xl border-2 border-emerald-200 bg-white p-4">
          <p className="mb-2 flex items-center gap-2 text-sm font-black text-emerald-700">
            ✍️ {t("acct.bills.sign.canvas")}
          </p>
          <SignatureCanvas onChange={setSig} color="#065F46" height={220} />
        </div>
        <MyBillSignTerms terms={terms} setTerms={setTerms} />
      </div>
      <MyBillSignActions busy={busy} canConfirm={Boolean(name.trim() && sig && terms)}
                          onConfirm={confirm} onReject={reject} />
    </div>
  );
}
